/**
 * Email templates and sending — `docs/API.md` §8.
 *
 * Fully implemented server-side: list, folders, create, read, patch, delete,
 * duplicate, merge-fields, preview and send all live in `salescrmapi/src/
 * modules/emailTemplates`. `GET /email/sent` and the open-tracking pixel are
 * new — see `salescrmapi/docs/modules/emailInbox.model.md`.
 */
import apiClient from './axiosClient.js';
import { withMockFallback } from './mockFallback.js';

/** @param {object} [params] any of listTemplatesQuery's fields, e.g. `{ folder, q }` */
export function listTemplates(params = {}) {
  return withMockFallback(
    'GET /email/templates',
    async () => (await apiClient.get('/email/templates', { params })).data,
    () => ({
      data: [],
      page: { limit: params.limit ?? 50, next_cursor: null, prev_cursor: null, has_more: false, total: 0 },
    }),
  );
}

/** The folder names already in use, for the editor's folder picker. */
export function listFolders() {
  return withMockFallback(
    'GET /email/templates/folders',
    async () => (await apiClient.get('/email/templates/folders')).data,
    () => ({ data: [] }),
  );
}

/** @param {string} id */
export function getTemplate(id) {
  return withMockFallback(
    `GET /email/templates/${id}`,
    async () => (await apiClient.get(`/email/templates/${id}`)).data,
    () => null,
  );
}

/**
 * Create a template. Carries an Idempotency-Key so a double-submit cannot
 * create the same template twice.
 * @param {object} payload folder, name, subject, body_html, and any of
 *   description/purpose/language/is_shared/owner_id/include_signature/is_active
 */
export function createTemplate(payload) {
  return withMockFallback(
    'POST /email/templates',
    async () =>
      (await apiClient.post('/email/templates', payload, {
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      })).data,
    () => ({ ...payload, id: `TPL-${Math.floor(Math.random() * 90000 + 10000)}`, row_version: 1 }),
  );
}

/**
 * Update a template under optimistic locking (§1.7).
 * @param {string} id
 * @param {object} patch
 * @param {number} rowVersion
 */
export function updateTemplate(id, patch, rowVersion) {
  return withMockFallback(
    `PATCH /email/templates/${id}`,
    async () =>
      (await apiClient.patch(`/email/templates/${id}`, patch, {
        headers: { 'If-Match': `"${rowVersion}"` },
      })).data,
    () => ({ id, ...patch }),
  );
}

/**
 * Soft-delete a template. `reason` is optional here — unlike leads and
 * activities, docs/API.md does not mandate one for this endpoint.
 * @param {string} id
 * @param {{reason?:string, rowVersion?:number}} [options]
 */
export function deleteTemplate(id, { reason, rowVersion } = {}) {
  return withMockFallback(
    `DELETE /email/templates/${id}`,
    async () =>
      (await apiClient.delete(`/email/templates/${id}`, {
        data: { ...(reason ? { reason } : {}), ...(rowVersion ? { row_version: rowVersion } : {}) },
      })).data,
    () => ({ id, deleted: true }),
  );
}

/**
 * Copy a template under a new, free name. Usage stats start over.
 * @param {string} id
 * @param {string} name
 */
export function duplicateTemplate(id, name) {
  return withMockFallback(
    `POST /email/templates/${id}/duplicate`,
    async () =>
      (await apiClient.post(`/email/templates/${id}/duplicate`, { name }, {
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      })).data,
    () => ({ id: `TPL-${Math.floor(Math.random() * 90000 + 10000)}`, name, row_version: 1 }),
  );
}

/** The merge-field catalogue the composer offers, grouped by family. */
export function getMergeFields() {
  return withMockFallback(
    'GET /email/merge-fields',
    async () => (await apiClient.get('/email/merge-fields')).data,
    () => ({ families: [] }),
  );
}

/**
 * Resolve a template (or unsaved subject/body) against a lead or contact,
 * without sending.
 * @param {{template_id?:string, subject?:string, body_html?:string,
 *          context:{lead_id?:string, contact_id?:string}}} body
 */
export function previewEmail(body) {
  return withMockFallback(
    'POST /email/preview',
    async () => (await apiClient.post('/email/preview', body)).data,
    () => ({
      subject: body.subject ?? '', body_html: body.body_html ?? '',
      resolved_tokens: {}, unresolved_tokens: [], warnings: [],
    }),
  );
}

/**
 * Enqueue a send — `POST /email/send` only queues (§1.8: `crm.activity_email`
 * with `status='queued'`); `jobs/workers/commSender.worker.js` makes the real
 * SendGrid/SMTP call. `context` must resolve to a lead, account, opportunity,
 * quotation or campaign — a bare `contact_id` isn't an anchor by itself
 * unless that contact belongs to an account.
 * @param {{to:string[], cc?:string[], bcc?:string[], subject?:string, body_html?:string,
 *          template_id?:string, context?:object}} body
 */
export function sendEmail(body) {
  return withMockFallback(
    'POST /email/send',
    async () =>
      (await apiClient.post('/email/send', body, {
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      })).data,
    () => { throw new Error('Sending email is not available in this build.'); },
  );
}

/**
 * The Email screen's inbox list — every send, most recent first, with whether
 * it's been opened.
 * @param {object} [params] any of listSentQuery's fields, e.g.
 *   `{ status, opened, sent_from, sent_to, q, lead_id, account_id, ... }`
 */
export function listSentEmails(params = {}) {
  return withMockFallback(
    'GET /email/sent',
    async () => (await apiClient.get('/email/sent', { params })).data,
    () => ({
      data: [],
      page: { limit: params.limit ?? 25, next_cursor: null, prev_cursor: null, has_more: false, total: 0 },
    }),
  );
}
